import { useEffect, useRef, useState } from "react";
import {
  createAdminProduct,
  deleteAdminProduct,
  fetchAdminProducts,
  resolveMediaUrl,
  updateAdminProduct,
  uploadAdminProductImage,
} from "../api";
import { navigateAdminSection } from "./adminRouting";

export const PRODUCT_CATEGORY_LABELS = {
  coffee: "Kávé",
  homemade: "Házi finomságok",
  merch: "Ajándék & merch",
  other: "Egyéb",
};

const IMAGE_MAX_SIDE = 1400;

const EMPTY_FORM = {
  nameHu: "",
  nameEn: "",
  descriptionHu: "",
  descriptionEn: "",
  unitLabel: "",
  priceHuf: "",
  category: "homemade",
  sortOrder: 0,
  visible: true,
  available: true,
};

function formatHuf(value) {
  return new Intl.NumberFormat("hu-HU", {
    style: "currency",
    currency: "HUF",
    maximumFractionDigits: 0,
  }).format(value || 0);
}

// Downscale in the browser so uploads stay small (phone photos are often 5–10 MB).
function readImageAsJpeg(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, IMAGE_MAX_SIDE / Math.max(img.width, img.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL("image/jpeg", 0.85));
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("invalid_image"));
    };
    img.src = url;
  });
}

function productToForm(product) {
  return {
    nameHu: product.nameHu,
    nameEn: product.nameEn,
    descriptionHu: product.descriptionHu,
    descriptionEn: product.descriptionEn,
    unitLabel: product.unitLabel,
    priceHuf: String(product.priceHuf),
    category: product.category,
    sortOrder: product.sortOrder,
    visible: product.visible,
    available: product.available,
  };
}

const ERROR_TEXT = {
  invalid_name: "Add meg a termék magyar nevét.",
  invalid_price: "Az ár legyen egész szám forintban (legalább 1 Ft).",
  invalid_image: "A kép nem olvasható. JPG, PNG vagy WebP fájlt válassz.",
  image_too_large: "A kép túl nagy.",
};

function ProductEditor({ token, product, onSaved, onCancel }) {
  const [form, setForm] = useState(product ? productToForm(product) : EMPTY_FORM);
  const [imageData, setImageData] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef(null);
  const isNew = !product;
  const previewSrc = imageData || resolveMediaUrl(product?.imageUrl);

  function update(field) {
    return (event) => {
      const { type, checked, value } = event.target;
      setForm((current) => ({ ...current, [field]: type === "checkbox" ? checked : value }));
    };
  }

  async function handleFile(event) {
    const file = event.target.files?.[0];
    if (!file) return;
    setError("");
    try {
      setImageData(await readImageAsJpeg(file));
    } catch {
      setError(ERROR_TEXT.invalid_image);
    }
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (saving) return;
    setSaving(true);
    setError("");

    const payload = {
      ...form,
      priceHuf: Number(form.priceHuf),
      sortOrder: Number(form.sortOrder) || 0,
    };

    try {
      const saved = isNew
        ? await createAdminProduct(token, payload)
        : await updateAdminProduct(token, product.id, payload);
      let result = saved.product;
      if (imageData) {
        result = (await uploadAdminProductImage(token, result.id, imageData)).product;
      }
      onSaved(result, isNew);
    } catch (err) {
      setError(ERROR_TEXT[err.message] || "A mentés sikertelen.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="admin-settings-card admin-product-editor">
      <h2 className="admin-shop-shipping-title">
        {isNew ? "Új termék" : `Szerkesztés: ${product.nameHu}`}
      </h2>

      <form className="admin-product-form" onSubmit={handleSubmit}>
        <div className="admin-product-image-field">
          <button
            type="button"
            className="admin-product-image-drop"
            onClick={() => fileRef.current?.click()}
          >
            {previewSrc ? (
              <img src={previewSrc} alt="" />
            ) : (
              <span>Kép feltöltése</span>
            )}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            hidden
            onChange={handleFile}
          />
          <p className="admin-settings-note">
            Kattints a cseréhez. Négyzetes vagy álló kép mutat a legjobban.
          </p>
        </div>

        <div className="admin-product-fields">
          <label className="admin-shop-field">
            <span>Név (magyar) *</span>
            <input value={form.nameHu} onChange={update("nameHu")} maxLength={120} required />
          </label>
          <label className="admin-shop-field">
            <span>Név (angol)</span>
            <input value={form.nameEn} onChange={update("nameEn")} maxLength={120} />
          </label>
          <label className="admin-shop-field admin-product-field--wide">
            <span>Leírás (magyar)</span>
            <textarea
              rows={3}
              value={form.descriptionHu}
              onChange={update("descriptionHu")}
              maxLength={1000}
            />
          </label>
          <label className="admin-shop-field admin-product-field--wide">
            <span>Leírás (angol)</span>
            <textarea
              rows={3}
              value={form.descriptionEn}
              onChange={update("descriptionEn")}
              maxLength={1000}
            />
          </label>
          <label className="admin-shop-field">
            <span>Ár (Ft) *</span>
            <input
              type="number"
              min="1"
              step="1"
              value={form.priceHuf}
              onChange={update("priceHuf")}
              required
            />
          </label>
          <label className="admin-shop-field">
            <span>Kiszerelés</span>
            <input
              value={form.unitLabel}
              onChange={update("unitLabel")}
              maxLength={40}
              placeholder="pl. 250 g"
            />
          </label>
          <label className="admin-shop-field">
            <span>Kategória</span>
            <select value={form.category} onChange={update("category")}>
              {Object.entries(PRODUCT_CATEGORY_LABELS).map(([id, label]) => (
                <option key={id} value={id}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <label className="admin-shop-field">
            <span>Sorrend</span>
            <input type="number" step="1" value={form.sortOrder} onChange={update("sortOrder")} />
          </label>
          <div className="admin-product-toggles admin-product-field--wide">
            <label className="admin-shop-toggle">
              <input type="checkbox" checked={form.visible} onChange={update("visible")} />
              <span>Látható a webshopban</span>
            </label>
            <label className="admin-shop-toggle">
              <input type="checkbox" checked={form.available} onChange={update("available")} />
              <span>Elérhető (kikapcsolva: „Elfogyott”)</span>
            </label>
          </div>
        </div>

        {error ? <p className="admin-settings-error">{error}</p> : null}

        <div className="admin-product-form-actions">
          <button type="button" className="admin-product-secondary" onClick={onCancel}>
            Mégse
          </button>
          <button type="submit" className="newsletter-modal-submit" disabled={saving}>
            {saving ? "Mentés…" : "Mentés"}
          </button>
        </div>
      </form>
    </section>
  );
}

function AdminProducts({ token }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | product
  const [busyId, setBusyId] = useState(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;

    async function load() {
      setLoading(true);
      try {
        const data = await fetchAdminProducts(token);
        if (!cancelled) setProducts(data.products || []);
      } catch {
        if (!cancelled) setError("Nem sikerült betölteni a termékeket.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [token]);

  function handleSaved(product, isNew) {
    setProducts((current) =>
      isNew ? [product, ...current] : current.map((p) => (p.id === product.id ? product : p)),
    );
    setEditing(null);
    setMessage(isNew ? "Termék létrehozva." : "Termék mentve.");
  }

  async function toggle(product, field) {
    setBusyId(product.id);
    setMessage("");
    setError("");
    try {
      const data = await updateAdminProduct(token, product.id, { [field]: !product[field] });
      setProducts((current) => current.map((p) => (p.id === product.id ? data.product : p)));
    } catch {
      setError("A módosítás sikertelen.");
    } finally {
      setBusyId(null);
    }
  }

  async function handleDelete(product) {
    if (!window.confirm(`Biztosan törlöd: ${product.nameHu}?`)) return;
    setBusyId(product.id);
    setMessage("");
    setError("");
    try {
      await deleteAdminProduct(token, product.id);
      setProducts((current) => current.filter((p) => p.id !== product.id));
      setMessage("Termék törölve.");
    } catch {
      setError("A törlés sikertelen.");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <>
      <header className="admin-dash-header">
        <div>
          <h1 className="admin-dash-title">Termékek</h1>
          <p className="admin-dash-subtitle">
            A webshop termékei. A Mizu Coffee árát és a szállítást a{" "}
            <button
              type="button"
              className="admin-product-inline-link"
              onClick={() => navigateAdminSection("shop")}
            >
              Webshop
            </button>{" "}
            fülön állítod.
          </p>
        </div>
        {editing ? null : (
          <button
            type="button"
            className="newsletter-modal-submit admin-product-new"
            onClick={() => {
              setMessage("");
              setEditing("new");
            }}
          >
            + Új termék
          </button>
        )}
      </header>

      {editing ? (
        <ProductEditor
          key={editing === "new" ? "new" : editing.id}
          token={token}
          product={editing === "new" ? null : editing}
          onSaved={handleSaved}
          onCancel={() => setEditing(null)}
        />
      ) : null}

      {message ? <p className="admin-settings-message">{message}</p> : null}
      {error ? <p className="admin-settings-error">{error}</p> : null}

      {loading ? (
        <p className="admin-dash-empty">Betöltés…</p>
      ) : products.length === 0 ? (
        <p className="admin-dash-empty">Még nincs termék. Az „Új termék” gombbal adhatsz hozzá.</p>
      ) : (
        <ul className="admin-product-list">
          {products.map((product) => {
            const busy = busyId === product.id;
            return (
              <li
                key={product.id}
                className={product.visible ? "admin-product-card" : "admin-product-card is-hidden"}
              >
                <div className="admin-product-thumb">
                  {product.imageUrl ? (
                    <img src={resolveMediaUrl(product.imageUrl)} alt="" loading="lazy" />
                  ) : (
                    <span>Nincs kép</span>
                  )}
                </div>
                <div className="admin-product-info">
                  <p className="admin-product-name">{product.nameHu}</p>
                  <p className="admin-product-meta">
                    {formatHuf(product.priceHuf)}
                    {product.unitLabel ? ` · ${product.unitLabel}` : ""}
                    {` · ${PRODUCT_CATEGORY_LABELS[product.category] || product.category}`}
                  </p>
                  <div className="admin-product-chips">
                    <button
                      type="button"
                      className={product.visible ? "admin-product-chip is-on" : "admin-product-chip"}
                      disabled={busy}
                      onClick={() => toggle(product, "visible")}
                      title="Kattintásra vált"
                    >
                      {product.visible ? "Látható" : "Rejtett"}
                    </button>
                    <button
                      type="button"
                      className={product.available ? "admin-product-chip is-on" : "admin-product-chip is-warn"}
                      disabled={busy}
                      onClick={() => toggle(product, "available")}
                      title="Kattintásra vált"
                    >
                      {product.available ? "Elérhető" : "Elfogyott"}
                    </button>
                  </div>
                </div>
                <div className="admin-product-actions">
                  <button
                    type="button"
                    className="admin-product-secondary"
                    disabled={busy}
                    onClick={() => {
                      setMessage("");
                      setEditing(product);
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                  >
                    Szerkesztés
                  </button>
                  <button
                    type="button"
                    className="admin-orders-delete"
                    disabled={busy}
                    onClick={() => handleDelete(product)}
                  >
                    Törlés
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </>
  );
}

export default AdminProducts;
